import { createListenerMiddleware } from '@reduxjs/toolkit'
import type { AppDispatch, RootState } from '@/store/store'
import { MedSummaryEntryEditActions } from './entries-edit.slice'
import { MedSummaryEntriesActions } from './entries.slice'
import { updateMedSummaryAssetEntry } from '@/medical-billing-summaries/asset/fetch/update-med-summary-asset-entry'
import { MedSummaryEntryUpdate } from './schemas'
import { validateField } from '../utils/validation'
import { prepareValueForSave, pendingValueEqualsOriginal } from '../utils/edit-helpers'
import { showGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType } from '@/constants/constants-ui'
import { getAnalyticsApiErrorProps } from '@/analytics/utils/analytics-helpers'

// Duration to display validation error before auto-exiting edit mode (in milliseconds)
const VALIDATION_ERROR_DISPLAY_DURATION_MS = 800

export const medSummaryEditListenerMiddleware = createListenerMiddleware()
const startListener = medSummaryEditListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()

/**
 * Listener: Save Field Edit
 *
 * Validates and saves edits to medical billing summary entry fields.
 * Validates the field edit, sends the update to the living-asset endpoint, and updates state
 * based on success or failure.
 */
startListener({
  actionCreator: MedSummaryEntryEditActions.saveFieldEdit,
  effect: async (action, listenerApi) => {
    const state = listenerApi.getState().medSummaryEntryEditState
    const activeEdit = state.activeEdit

    // If there is no active edit or the edit is already saving, return
    if (!activeEdit || activeEdit.isSaving) return

    const { parentId, entryId, fieldKey, pendingValue, originalValue } = activeEdit
    const autoCancelOnError = action.payload?.autoCancelOnError ?? false

    // Mark as saving
    listenerApi.dispatch(MedSummaryEntryEditActions.startSaveField())

    // Trim whitespace from pending value
    const trimmedValue = pendingValue.trim()

    // If value hasn't effectively changed, just close edit mode
    if (pendingValueEqualsOriginal(fieldKey, originalValue, trimmedValue)) {
      listenerApi.dispatch(MedSummaryEntryEditActions.cancelEditField())
      return
    }

    // Validate trimmed value before saving
    const validationError = validateField(fieldKey, trimmedValue)
    if (validationError) {
      listenerApi.dispatch(MedSummaryEntryEditActions.setValidationError({ message: validationError, autoDismiss: autoCancelOnError }))

      // Auto-cancel the edit after a delay
      // Desired UX: Show the validation error briefly, then exit edit mode
      if (autoCancelOnError) {
        await new Promise((resolve) => setTimeout(resolve, VALIDATION_ERROR_DISPLAY_DURATION_MS))
        listenerApi.dispatch(MedSummaryEntryEditActions.cancelEditField())
      }
      return
    }

    try {
      // Prepare/transform the value for sending to the API
      const valueToSave = prepareValueForSave(fieldKey, trimmedValue)

      // Build update object
      const updates: MedSummaryEntryUpdate = {
        [fieldKey]: valueToSave,
      }

      const updatedEntry = await updateMedSummaryAssetEntry({ assetId: parentId, entryId, updates })

      // Update entry in store with full response
      listenerApi.dispatch(MedSummaryEntriesActions.updateEntry({ parentId, entry: updatedEntry }))
      listenerApi.dispatch(MedSummaryEntryEditActions.saveFieldSucceeded({ parentId, entryId, fieldKey }))
    } catch (error) {
      // Update failed - show toast
      listenerApi.dispatch(showGlobalToast({ type: GlobalToastType.ERROR, message: "Couldn't save changes. Please try again.", durationMs: 3000 }))
      listenerApi.dispatch(MedSummaryEntryEditActions.saveFieldFailed({ parentId, entryId, fieldKey, ...getAnalyticsApiErrorProps(error) }))
    } finally {
      // Always complete save to close edit mode
      listenerApi.dispatch(MedSummaryEntryEditActions.completeSaveField())
    }
  },
})
